import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { MapContainer, TileLayer, ZoomControl } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'react-leaflet-cluster/lib/assets/MarkerCluster.css';
import 'react-leaflet-cluster/lib/assets/MarkerCluster.Default.css';
import salesforceApi from '../services/salesforceApi';
import BackToHomeLink from '../components/BackToHomeLink';
import JobMapFilters from '../components/JobMapFilters';
import JobMapMarkers from '../components/JobMapMarkers';
import MapResetControl from '../components/MapResetControl';
import MapEventHandler from '../components/MapEventHandler';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  margin-bottom: 1rem;
  color: #28346c;
`;

const PageDescription = styled.p`
  color: #7f8c8d;
  font-size: 1.1rem;
`;

const PageLayout = styled.div`
  display: flex;
  gap: 2rem;
  height: 600px;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
  }
`;

const FilterSidebar = styled.div`
  flex: 0 0 280px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 1.5rem;
  overflow-y: auto;
  scrollbar-width: thin;
  height: 450px;
  align-self: flex-start;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(52, 152, 219, 0.3);
    border-radius: 3px;
    transition: background 0.2s ease;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(52, 152, 219, 0.6);
  }

  @media (max-width: 768px) {
    position: static;
    width: 100%;
    max-height: ${({ $isCollapsed }) => ($isCollapsed ? '52px' : 'none')};
    overflow: ${({ $isCollapsed }) => ($isCollapsed ? 'hidden' : 'visible')};
    padding: ${({ $isCollapsed }) => ($isCollapsed ? '.75rem 1rem' : '1.5rem')};
    margin-bottom: ${({ $isCollapsed }) => ($isCollapsed ? '.5rem' : '1rem')};
    transition: all 0.3s ease;
    height: auto;
    flex: 0 0 auto;
  }
`;

const MapContainerStyled = styled.div`
  flex: 1;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  position: relative;
  
  @media (max-width: 768px) {
    height: 60vh;
    min-height: 400px;
    width: 100%;
    flex: none;
  }
  
  .leaflet-container {
    height: 100%;
    width: 100%;
  }
  
  /* Mobile touch optimizations */
  @media (max-width: 768px) {
    .leaflet-container {
      touch-action: pan-x pan-y;
      position: relative !important;
      height: 100% !important;
      width: 100% !important;
    }
    
    .leaflet-control-zoom {
      margin-bottom: 20px !important;
      margin-right: 20px !important;
    }
    
    .leaflet-control-zoom a {
      width: 36px !important;
      height: 36px !important;
      line-height: 36px !important;
      font-size: 20px !important;
    }
    
    .leaflet-popup-content-wrapper {
      border-radius: 12px !important;
    }
    
    .leaflet-popup-content {
      font-size: 14px !important;
      line-height: 1.4 !important;
    }
  }

  /* Override default cluster styles to remove default backgrounds */
  .marker-cluster-small,
  .marker-cluster-medium,
  .marker-cluster-large {
    background: none !important;
    
    div {
      background: none !important;
    }
  }

  /* Remove default cluster spans */
  .marker-cluster span {
    display: none;
  }

  /* Ensure our custom cluster icons are visible */
  .heat-map-cluster,
  .heat-map-marker {
    z-index: 400 !important;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: #7f8c8d;
`;

const LoadingDots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
`;

const LoadingDot = styled.div`
  width: 1rem;
  height: 1rem;
  background-color: #f47424;
  border-radius: 50%;
  animation: bounce-pulse 1.4s infinite ease-in-out;
  animation-delay: ${props => props.$delay || '0s'};

  @keyframes bounce-pulse {
    0%, 80%, 100% {
      transform: scale(0.6);
      opacity: 0.6;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #e74c3c;
  border: 1px solid #e74c3c;
  border-radius: 8px;
`;

const NoJobsMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #7f8c8d;
  font-size: 1.1rem;
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '1rem')};
`;

const FilterHeading = styled.h2`
  font-size: 1.2rem;
  margin: 0;
  color: #2c3e50;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const FilterContent = styled.div`
  @media (max-width: 768px) {
    display: ${({ $isCollapsed }) => ($isCollapsed ? 'none' : 'block')};
  }
`;

const ToggleButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    font-size: 0.85rem;
    font-weight: 600;
    color: #28346c;
    cursor: pointer;
    padding: 0;
  }
`;

const ChevronDownIcon = styled.span`
  margin-left: 0.5rem;
  font-size: 0.75rem;
`;

const XIcon = styled.span`
  margin-left: 0.5rem;
  font-size: 1rem;
  font-weight: bold;
`;


const ClearAllButton = styled.button`
  background: #e74c3c;
  border: none;
  color: white;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  transition: background-color 0.3s;
  font-weight: 600;
  flex-shrink: 0;
  
  &:hover {
    background-color: #c0392b;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.35rem 0.7rem;
    min-height: 32px;
    min-width: 80px;
  }
`;

const FilterTag = styled.div`
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  background-color: ${({ category }) => {
    switch(category) {
      case 'jobTitles':
        return '#e3f5e1';
      case 'specialties':
        return '#fce4ec';
      case 'shifts':
        return '#fff3e0';
      default:
        return '#e1f0fa';
    }
  }};
  color: black;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.2rem 0.6rem;
  }
`;

const RemoveFilterButton = styled.button`
  background: none;
  border: none;
  margin-left: 0.5rem;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  color: inherit;
  
  &:hover {
    color: #e74c3c;
  }
`;

const JobMap = ({ embed = false }) => {
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [hasStateChanged, setHasStateChanged] = useState(false);
  const [hasMapMoved, setHasMapMoved] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    jobTitles: [],
    specialties: [],
    shifts: []
  });
  const [filterSidebarCollapsed, setFilterSidebarCollapsed] = useState(false);
  const [initialFilters, setInitialFilters] = useState(null);
  const mapRef = useRef(null);
  const filtersRef = useRef(null);
  const initialJobsRef = useRef([]);

  useEffect(() => {
    document.title = "Job Location Map | Find Healthcare Jobs Near You with Capstone";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Explore healthcare job opportunities on our interactive map. Find nursing, therapy, and allied health positions across the United States with Capstone Healthcare Staffing.');
    }
  }, []);

  // Handle mobile resize and map invalidation
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        setTimeout(() => {
          mapRef.current.invalidateSize();
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        setError('');
        const jobsData = await salesforceApi.getJobs();

        if (!jobsData || jobsData.length === 0) {
          setJobs([]);
          setFilteredJobs([]);
        } else {
          // Filter jobs to only include those with geolocation data
          const jobsWithLocation = jobsData.filter(job => {
            // Check if job has geolocation field
            if (job.Geolocation) {
              try {
                // Handle if Geolocation is already parsed object
                if (typeof job.Geolocation === 'object' && job.Geolocation.latitude && job.Geolocation.longitude) {
                  return true;
                }

                // Handle if Geolocation is JSON string
                if (typeof job.Geolocation === 'string') {
                  const parsed = JSON.parse(job.Geolocation);
                  return parsed.latitude && parsed.longitude;
                }
              } catch (error) {
                return false;
              }
            }
            return false;
          });

          console.log(`Loaded ${jobsData.length} total jobs, ${jobsWithLocation.length} have geolocation data`);

          // Additional debugging
          const uniqueCities = new Set();
          jobsWithLocation.forEach(job => {
            if (job.city && job.State) {
              uniqueCities.add(`${job.city}, ${job.State}`);
            }
          });
          console.log(`Jobs are distributed across ${uniqueCities.size} unique cities`);

          setJobs(jobsWithLocation);
          setFilteredJobs(jobsWithLocation);
          initialJobsRef.current = jobsWithLocation;

          // Parse initial filters from URL params
          const urlFilters = {
            jobTitles: [],
            specialties: [],
            shifts: []
          };

          const titleParam = searchParams.get('title');
          if (titleParam) {
            urlFilters.jobTitles = titleParam.split(',');
          }

          const specialtyParam = searchParams.get('specialty');
          if (specialtyParam) {
            urlFilters.specialties = specialtyParam.split(',');
          }

          const shiftParam = searchParams.get('shift');
          if (shiftParam) {
            urlFilters.shifts = shiftParam.split(',');
          }

          // Set initial filters if any params are present
          if (urlFilters.jobTitles.length > 0 || urlFilters.specialties.length > 0 || urlFilters.shifts.length > 0) {
            setInitialFilters(urlFilters);
            setHasStateChanged(true);
          }
        }
      } catch (err) {
        setError(`Failed to load job listings: ${err.message}. Please check your network connection and try again.`);
        setJobs([]);
        setFilteredJobs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [searchParams]);

  const handleFilterChange = useCallback((newFilteredJobs) => {
    setFilteredJobs(newFilteredJobs);
    // Check if filtered jobs are different from initial jobs
    setHasStateChanged(newFilteredJobs.length !== initialJobsRef.current.length);
  }, []);

  const handleFiltersUpdate = useCallback((newFilters) => {
    setActiveFilters(newFilters);
    // Check if any filters are active
    const hasActiveFilters = Object.values(newFilters).some(filterArray => filterArray.length > 0);
    setHasStateChanged(hasActiveFilters);
  }, []);

  const handleRemoveFilter = useCallback((category, value) => {
    if (filtersRef.current && typeof filtersRef.current.removeFilter === 'function') {
      filtersRef.current.removeFilter(category, value);
    }
  }, []);

  const handleClearAllFilters = useCallback(() => {
    if (filtersRef.current && typeof filtersRef.current.resetFilters === 'function') {
      filtersRef.current.resetFilters();
    }
  }, []);

  const toggleFilterSidebar = () => {
    setFilterSidebarCollapsed(prev => !prev);
  };

  const handleMapChange = useCallback((hasMoved) => {
    setHasMapMoved(hasMoved);
  }, []);

  const handleReset = useCallback(() => {
    console.log('Reset button clicked!'); // Debug log
    
    // ONLY reset map view to initial position and zoom
    if (mapRef.current) {
      console.log('Map ref exists, resetting view'); // Debug log
      try {
        const defaultZoom = window.innerWidth <= 768 ? 3.75 : 4;
        mapRef.current.setView([39.8283, -98.5795], defaultZoom);
        mapRef.current.invalidateSize();
      } catch (error) {
        console.error('Error resetting map view:', error);
      }
    } else {
      console.log('Map ref is null!'); // Debug log
    }
    
    // Reset ONLY the map moved flag (keep filters and state as they are)
    setHasMapMoved(false);
  }, []);

  return (
    <PageContainer>
      {!embed && (
        <>
          <BackToHomeLink />
          <PageHeader>
            <PageTitle>Job Location Map</PageTitle>
            <PageDescription>
              Explore healthcare job opportunities across the United States. Use the filters to narrow down your search and click on map markers to view job details.
            </PageDescription>
          </PageHeader>
        </>
      )}

      {loading ? (
        <LoadingState>
          <LoadingDots>
            <LoadingDot $delay="0s" />
            <LoadingDot $delay="0.2s" />
            <LoadingDot $delay="0.4s" />
          </LoadingDots>
        </LoadingState>
      ) : error ? (
        <ErrorState>{error}</ErrorState>
      ) : jobs.length === 0 ? (
        <NoJobsMessage>No job locations available at this time. Please check back later.</NoJobsMessage>
      ) : (
        <PageLayout>
          <FilterSidebar $isCollapsed={filterSidebarCollapsed}>
            <FilterHeader $isCollapsed={filterSidebarCollapsed}>
              <FilterHeading>
                Refine Your Search
              </FilterHeading>
              <ToggleButton onClick={toggleFilterSidebar}>
                {filterSidebarCollapsed ? (
                  <>
                    Expand
                    <ChevronDownIcon>▼</ChevronDownIcon>
                  </>
                ) : (
                  <>
                    Collapse
                    <XIcon>×</XIcon>
                  </>
                )}
              </ToggleButton>
            </FilterHeader>
            
            <FilterContent $isCollapsed={filterSidebarCollapsed}>
              <JobMapFilters
                ref={filtersRef}
                jobs={jobs}
                onFilterChange={handleFilterChange}
                onFiltersUpdate={handleFiltersUpdate}
                initialFilters={initialFilters}
              />
            </FilterContent>
          </FilterSidebar>
          
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Active Filters Display - Above just the map */}
            {Object.entries(activeFilters).some(([_, values]) => values.length > 0) && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                marginBottom: '1rem',
                padding: '0 1rem',
                flexWrap: 'wrap',
                gap: '0.5rem'
              }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', flex: '1', minWidth: '0' }}>
                  {Object.entries(activeFilters)
                    .map(([category, values]) => 
                      values.map(value => (
                        <FilterTag 
                          key={`tag-${category}-${value}`}
                          category={category}
                        >
                          {value}
                          <RemoveFilterButton onClick={() => handleRemoveFilter(category, value)}>
                            ×
                          </RemoveFilterButton>
                        </FilterTag>
                      ))
                    )}
                </div>
                <ClearAllButton onClick={handleClearAllFilters}>
                  Clear All
                </ClearAllButton>
              </div>
            )}
            
            <MapContainerStyled>
              {/* Fallback for mobile map loading */}
              <div style={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                backgroundColor: '#f8f9fa', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                zIndex: 0
              }}>
                <div style={{ textAlign: 'center', color: '#666' }}>
                  <div>Loading Map...</div>
                </div>
              </div>
              
              <MapContainer
                ref={mapRef}
                center={[39.8283, -98.5795]} // Center of continental US
                zoom={window.innerWidth <= 768 ? 3.75 : 4}
                minZoom={window.innerWidth <= 768 ? 3 : 4}
                maxZoom={18}
                style={{ height: '100%', width: '100%', position: 'relative', zIndex: 1 }}
                attributionControl={false}
                zoomControl={false}
                touchZoom={true}
                dragging={true}
                tap={true}
                keyboard={false}
                zoomSnap={0.25}
                zoomDelta={0.5}
                wheelPxPerZoomLevel={120}
                whenReady={() => {
                  console.log('Map is ready for mobile');
                  // Force resize after map is ready
                  setTimeout(() => {
                    if (mapRef.current) {
                      mapRef.current.invalidateSize();
                      console.log('Map size invalidated for mobile');
                    }
                  }, 100);
                }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <ZoomControl position="bottomright" />
                <MapEventHandler onMapChange={handleMapChange} />
                <MapResetControl onReset={handleReset} visible={hasStateChanged || hasMapMoved} />
                <MarkerClusterGroup
                  chunkedLoading
                  clusterInitialized={(clusterGroup) => {
                    // Log total jobs across all clusters when initialized
                    setTimeout(() => {
                      const allMarkers = clusterGroup.getLayers();
                      let totalJobsInClusters = 0;
                      allMarkers.forEach(marker => {
                        if (marker.jobCount) {
                          totalJobsInClusters += marker.jobCount;
                        } else if (marker.jobs) {
                          totalJobsInClusters += marker.jobs.length;
                        } else {
                          totalJobsInClusters += 1;
                        }
                      });
                      console.log(`Total jobs displayed on map: ${totalJobsInClusters}`);
                    }, 100);
                  }}
                  maxClusterRadius={(zoom) => {
                    // Distance-based clustering that adapts to zoom level
                    // Start with smaller radius for better regional separation
                    if (zoom <= 4) return 50;   // Country view - show regional clusters
                    if (zoom <= 5) return 60;   // Multi-state regions
                    if (zoom <= 6) return 70;   // State level
                    if (zoom <= 7) return 50;   // State regions
                    if (zoom <= 8) return 40;   // Multi-city regions
                    if (zoom <= 10) return 30;  // City level
                    if (zoom <= 12) return 20;  // City districts
                    return 15; // Minimal clustering for very close zoom
                  }}
                  spiderfyOnMaxZoom={false} // Don't spiderfy, show popup instead
                  showCoverageOnHover={false}
                  zoomToBoundsOnClick={true} // Enable single-click zoom
                  animateAddingMarkers={false} // Better performance
                  disableClusteringAtZoom={13} // Stop clustering at city zoom level
                  removeOutsideVisibleBounds={true} // Performance optimization
                  animate={false} // Better performance for large datasets
                  iconCreateFunction={(cluster) => {
                    // Count total JOBS, not just markers
                    const childMarkers = cluster.getAllChildMarkers();
                    let totalJobs = 0;
                    
                    childMarkers.forEach(marker => {
                      // Access job count from marker properties
                      if (marker.jobCount) {
                        totalJobs += marker.jobCount;
                      } else if (marker.jobs) {
                        totalJobs += marker.jobs.length;
                      } else if (marker.options && marker.options.jobCount) {
                        totalJobs += marker.options.jobCount;
                      } else if (marker.options && marker.options.jobs) {
                        totalJobs += marker.options.jobs.length;
                      } else {
                        // Fallback - count as single job
                        totalJobs += 1;
                      }
                    });
                    
                    // Debug log for first few clusters
                    if (cluster._childCount <= 10) {
                      console.log(`Cluster has ${childMarkers.length} markers, ${totalJobs} total jobs`);
                    }
                    
                    const count = totalJobs;
                    let size, backgroundColor, fontSize;
                    
                    // Heat map style based on job count - optimized for mobile touch
                    if (count >= 50) {
                      size = 56;
                      backgroundColor = '#d73027'; // Dark red
                      fontSize = '15px';
                    } else if (count >= 20) {
                      size = 48;
                      backgroundColor = '#f46d43'; // Orange-red
                      fontSize = '14px';
                    } else if (count >= 10) {
                      size = 42;
                      backgroundColor = '#fdae61'; // Orange
                      fontSize = '13px';
                    } else if (count >= 5) {
                      size = 36;
                      backgroundColor = '#fee08b'; // Yellow-orange
                      fontSize = '12px';
                    } else if (count > 1) {
                      size = 32;
                      backgroundColor = '#e6f598'; // Light green
                      fontSize = '11px';
                    } else {
                      size = 28;
                      backgroundColor = '#3288bd'; // Blue for single jobs
                      fontSize = '10px';
                    }
                  
                    return L.divIcon({
                      html: `
                        <div style="
                          background-color: ${backgroundColor};
                          color: ${count > 4 ? 'white' : 'black'};
                          border-radius: 50%;
                          width: ${size}px;
                          height: ${size}px;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          font-weight: bold;
                          font-size: ${fontSize};
                          border: 3px solid white;
                          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
                          cursor: pointer;
                          position: relative;
                        ">
                          ${count}
                          <div style="
                            position: absolute;
                            top: -3px;
                            right: -3px;
                            width: 12px;
                            height: 12px;
                            background-color: #2c3e50;
                            border-radius: 50%;
                            border: 2px solid white;
                          "></div>
                        </div>
                      `,
                      className: 'heat-map-cluster',
                      iconSize: [size, size],
                      iconAnchor: [size/2, size/2]
                    });
                  }}
                  clusterMouseover={(event) => {
                    // Add hover effect
                    const icon = event.layer._icon;
                    if (icon) {
                      icon.style.transform = 'scale(1.1)';
                      icon.style.transition = 'transform 0.2s ease';
                    }
                  }}
                  clusterMouseout={(event) => {
                    // Remove hover effect
                    const icon = event.layer._icon;
                    if (icon) {
                      icon.style.transform = 'scale(1)';
                    }
                  }}
                  polygonOptions={{
                    fillColor: '#28346c',
                    color: '#28346c',
                    weight: 2,
                    opacity: 0.5,
                    fillOpacity: 0.1
                  }}
                >
                  <JobMapMarkers jobs={filteredJobs} />
                </MarkerClusterGroup>
              </MapContainer>
            </MapContainerStyled>
          </div>
        </PageLayout>
      )}
    </PageContainer>
  );
};

export default JobMap;